'use client';

import type { FC, ReactNode, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { cn } from '../utils/cn';
import { useScreenLayout } from './useScreenLayout';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { LegalSection } from '../screens/HomeScreen/components/LegalSection';

interface ScreenContentProps {
  children: ReactNode;
  className?: string;
  resetScrollOnMount?: boolean;
  scrollRef?: RefObject<HTMLDivElement | null>;
  scrollKey?: string;
  hideLegalSection?: boolean;
}

export const ScreenContent: FC<ScreenContentProps> = ({
  children,
  className,
  resetScrollOnMount = true,
  scrollRef: externalScrollRef,
  scrollKey,
  hideLegalSection = false,
}) => {
  const internalScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = externalScrollRef || internalScrollRef;
  const { disableSafeArea } = useScreenLayout();
  const isDesktop = useIsDesktop();

  useScrollRestoration({
    scrollKey: scrollKey || '',
    scrollRef,
    enabled: !!scrollKey,
  });

  useEffect(() => {
    if (!scrollKey && resetScrollOnMount && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [scrollKey, resetScrollOnMount, scrollRef]);

  return (
    <div
      ref={scrollRef}
      data-screen-scroll
      className={cn(
        'absolute top-0 start-0 end-0 bottom-0 overflow-y-auto overflow-x-hidden [container-type:size] [overflow-anchor:auto] [-webkit-overflow-scrolling:touch] pt-[max(var(--screen-fixed-top-height,0px),calc(var(--tg-viewport-content-safe-area-inset-top,0px)+var(--tg-viewport-safe-area-inset-top,0px)))] pb-[max(calc(var(--screen-fixed-bottom-height,0px)+var(--screen-fixed-bottom-lift,0px)+var(--screen-fixed-bottom-gap,0px)),var(--screen-tabbar-height,0px),calc(var(--tg-viewport-content-safe-area-inset-bottom,0px)+var(--tg-viewport-safe-area-inset-bottom,0px)))]',
        isDesktop && !hideLegalSection && 'flex flex-col',
        !isDesktop && 'bg-black',
        disableSafeArea && '!pt-[var(--screen-fixed-top-height,0px)] !pb-[max(calc(var(--screen-fixed-bottom-height,0px)+var(--screen-fixed-bottom-lift,0px)+var(--screen-fixed-bottom-gap,0px)),var(--screen-tabbar-height,0px))]',
        className
      )}
    >
      {children}
      {isDesktop && !hideLegalSection && (
        <div className="mt-auto">
          <LegalSection />
        </div>
      )}
    </div>
  );
};
